import { Pencil, Trash2 } from 'lucide-react';
import { Labeled, TextArea, Toggle } from '@tes/ui';
import { useEditor } from './editorStore';
import { useVariables } from '../components/variables';
import { PlacementSection, SignerFieldOptions, TextStyleSection } from './fieldSections';

export function PropertiesPanel() {
  const field = useEditor((s) => s.fields.find((f) => f.uid === s.selectedUid) ?? null);
  const roles = useEditor((s) => s.roles);
  const pageCount = useEditor((s) => s.pageSizes.length);
  const style = useEditor((s) => s.defaultTextStyle);
  const update = useEditor((s) => s.updateField);
  const remove = useEditor((s) => s.removeField);
  const openLibrary = useEditor((s) => s.setLibraryOpen);
  const { data: vars } = useVariables();

  if (!field) {
    return (
      <div className="p-4 text-sm text-slate-400">
        Select a field on the page to edit its properties.
      </div>
    );
  }

  const def = field.kind === 'variable' ? vars?.find((v) => v.key === field.key) : undefined;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          {field.kind === 'variable'
            ? 'Variable'
            : field.kind === 'static'
              ? 'Static text'
              : `Signer field · ${field.type}`}
        </h3>
        <button
          onClick={() => remove(field.uid)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>

      {field.kind === 'static' ? (
        <>
          <Labeled label="Text (appears on every document)">
            <TextArea
              value={field.text ?? ''}
              onChange={(e) => update(field.uid, { text: e.target.value })}
              rows={3}
              placeholder="accounts@topendsolar.com"
            />
          </Labeled>
          {!field.text?.trim() && (
            <p className="text-xs text-red-600">
              Static text can&apos;t be empty — saving will be rejected.
            </p>
          )}
          <Toggle
            label="Multiline (wrap + fit height)"
            checked={!!field.multiline}
            onChange={(v) => update(field.uid, { multiline: v })}
          />
          <p className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
            Baked into the template — n8n sends nothing for this, and it never appears in the
            contract. Change it here and republish to update future documents.
          </p>
        </>
      ) : field.kind === 'variable' ? (
        <>
          {/* Variable identity/settings live in the library — read-only here. */}
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-mono text-xs text-slate-500">{field.key}</div>
            <div className="text-sm font-medium text-slate-800">{def?.label ?? field.key}</div>
            <div className="mt-1 text-xs text-slate-500">
              {def ? (def.required ? 'Required' : 'Optional') : 'Not in library!'}
              {def?.type === 'checkbox'
                ? ` · checkbox · default: ${def.default === 'true' ? 'ticked' : 'unticked'}`
                : def?.default
                  ? ` · default: “${def.default}”`
                  : ''}
            </div>
          </div>
          <button
            onClick={() => openLibrary(true)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            <Pencil size={13} /> Edit in library…
          </button>
          {/* A checkbox variable stamps a mark sized to its box — there are no
              lines to wrap, so the control would be a lie. */}
          {def?.type !== 'checkbox' && (
            <Toggle
              label="Multiline (wrap + fit height)"
              checked={!!field.multiline}
              onChange={(v) => update(field.uid, { multiline: v })}
            />
          )}
        </>
      ) : (
        <SignerFieldOptions field={field} update={update} roles={roles} />
      )}

      <TextStyleSection field={field} style={style} update={update} variableType={def?.type} />

      <PlacementSection field={field} pageCount={pageCount} update={update} />
    </div>
  );
}
